import * as moment from 'moment';
import { IMetric } from '../interfaces';

export interface IDailyMetricPoint {
  dateKey: string;
  dateOfTaking: string;
  value: number | null;
  metric: IMetric | null;
}

/**
 * Computes the median of numeric values.
 */
export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  return sorted[mid];
}

/**
 * Builds one point per calendar day in [startDate, endDate].
 * Day value is the median of that day's STI readings; missing days are null
 * so ApexCharts renders a visible discontinuity (does not connect across gaps).
 */
export function buildDailyMedianMetrics(
  metrics: IMetric[],
  startDate?: string | null,
  endDate?: string | null
): IDailyMetricPoint[] {
  if (!metrics?.length && (!startDate || !endDate)) {
    return [];
  }

  const byDay = new Map<string, IMetric[]>();

  for (const metric of metrics || []) {
    if (metric.value === null || metric.value === undefined) {
      continue;
    }

    const dayKey = moment(metric.dateOfTaking).format('YYYY-MM-DD');
    const bucket = byDay.get(dayKey) || [];
    bucket.push(metric);
    byDay.set(dayKey, bucket);
  }

  const rangeStart = startDate
    ? moment(startDate).startOf('day')
    : byDay.size
    ? moment(Math.min(...[...byDay.keys()].map(k => moment(k).valueOf())))
    : null;
  const rangeEnd = endDate
    ? moment(endDate).startOf('day')
    : byDay.size
    ? moment(Math.max(...[...byDay.keys()].map(k => moment(k).valueOf())))
    : null;

  if (!rangeStart || !rangeEnd || rangeStart.isAfter(rangeEnd)) {
    return [];
  }

  const points: IDailyMetricPoint[] = [];
  const cursor = rangeStart.clone();

  while (!cursor.isAfter(rangeEnd)) {
    const dateKey = cursor.format('YYYY-MM-DD');
    const dayMetrics = byDay.get(dateKey) || [];

    if (!dayMetrics.length) {
      points.push({
        dateKey,
        dateOfTaking: `${dateKey}T00:00:00`,
        value: null,
        metric: null,
      });
    } else {
      const values = dayMetrics.map(m => m.value as number);
      const medianValue = median(values);
      const representative =
        dayMetrics.reduce((closest, current) => {
          const closestDiff = Math.abs((closest.value as number) - medianValue);
          const currentDiff = Math.abs((current.value as number) - medianValue);
          return currentDiff < closestDiff ? current : closest;
        }, dayMetrics[0]) || null;

      points.push({
        dateKey,
        dateOfTaking: representative?.dateOfTaking || `${dateKey}T00:00:00`,
        value: medianValue,
        metric: representative,
      });
    }

    cursor.add(1, 'day');
  }

  return points;
}

/**
 * Builds the dashed "gap bridge" line(s) that connect two known values across
 * missing days. Gap endpoints keep their real values and missing days are
 * linearly interpolated; everything else is null. Leading/trailing gaps stay
 * empty because there is no value on one side to connect to.
 *
 * Consecutive gaps are split across TWO alternating series so the bridge never
 * draws a segment on top of the solid data line. When exactly two real days sit
 * between two gaps, each of those days is an endpoint of a different gap; keeping
 * them in one series would connect them with a dashed segment that overlaps the
 * solid line. Alternating gaps by parity keeps those shared endpoints in
 * separate series, so dashed and solid lines never cover each other.
 */
export function buildGapBridgeSeries(
  values: (number | null)[]
): (number | null)[][] {
  const seriesA: (number | null)[] = values.map(() => null);
  const seriesB: (number | null)[] = values.map(() => null);
  let prevIndex = -1;
  let gapIndex = 0;

  values.forEach((value, index) => {
    if (value === null || value === undefined) {
      return;
    }

    if (prevIndex !== -1 && index - prevIndex > 1) {
      const target = gapIndex % 2 === 0 ? seriesA : seriesB;
      const prevValue = values[prevIndex] as number;
      const step = (value - prevValue) / (index - prevIndex);

      for (let i = prevIndex; i <= index; i++) {
        target[i] = prevValue + step * (i - prevIndex);
      }

      gapIndex++;
    }

    prevIndex = index;
  });

  return [seriesA, seriesB];
}
